// 2D vector math for screen-space coordinates: y grows downward, angles are
// in radians, heading 0 points along +x, and positive rotation is clockwise
// on screen.

export interface Vec2 {
  x: number;
  y: number;
}

function add(a: Vec2, b: Vec2): Vec2 {
  return { x: a.x + b.x, y: a.y + b.y };
}

function sub(a: Vec2, b: Vec2): Vec2 {
  return { x: a.x - b.x, y: a.y - b.y };
}

function scale(a: Vec2, s: number): Vec2 {
  return { x: a.x * s, y: a.y * s };
}

function dot(a: Vec2, b: Vec2): number {
  return a.x * b.x + a.y * b.y;
}

function len(a: Vec2): number {
  return Math.sqrt(a.x * a.x + a.y * a.y);
}

function norm(a: Vec2): Vec2 {
  const l = len(a);
  if (l === 0) return { x: 0, y: 0 };
  return { x: a.x / l, y: a.y / l };
}

// Rotates point p by ang radians around the origin. Positive ang is
// clockwise on screen, matching fromAngle.
function rot(p: Vec2, ang: number): Vec2 {
  const c = Math.cos(ang);
  const s = Math.sin(ang);
  return { x: p.x * c - p.y * s, y: p.x * s + p.y * c };
}

// Unit vector pointing in direction ang. ang 0 is +x, ang PI/2 is +y
// (down on screen), matching the clockwise convention.
function fromAngle(ang: number): Vec2 {
  return { x: Math.cos(ang), y: Math.sin(ang) };
}

function lerp(a: Vec2, b: Vec2, t: number): Vec2 {
  return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
}

function dist(a: Vec2, b: Vec2): number {
  return len(sub(a, b));
}

export const v = { add, sub, scale, dot, len, norm, rot, fromAngle, lerp, dist };
